'use client';

import type { PusherIntegrationConfigType } from '@unifyapps/defs/types/interface';
import { useCallback, useRef } from 'react';
import type { PusherCallback } from '../../consts/pusher/callbacks';
import type { PusherClient } from '../../consts/pusher/sdk';
import { getPusherConstructor } from '../../utils/pusher/getSdk';
import { loadPusherSdk } from '../../utils/pusher/loadSdk';
import { usePusherConnectEvents } from './useConnectEvents';

interface UsePusherSdkLifecycleParams {
  pusherIntegrationConfig?: PusherIntegrationConfigType;
  publishPusherCallback: (eventName: 'onPusherCallback', event: PusherCallback) => void;
}

export function usePusherSdkLifecycle({
  pusherIntegrationConfig,
  publishPusherCallback,
}: UsePusherSdkLifecycleParams) {
  const clientRef = useRef<PusherClient | undefined>(undefined);
  const authValueRef = useRef<string | undefined>(undefined);
  const pendingInitRef = useRef<Promise<boolean> | undefined>(undefined);

  const ensureSdkLoaded = useCallback(() => loadPusherSdk(), []);

  const onConnectionError = useCallback(() => {
    clientRef.current?.disconnect();
    clientRef.current = undefined;
  }, []);

  const { bindConnectEvents } = usePusherConnectEvents({ publishPusherCallback, onConnectionError });

  const initializePusher = useCallback(
    (authValue: string): Promise<boolean> => {
      if (!pusherIntegrationConfig) return Promise.resolve(false);

      // headersProvider reads this on every authorization request, so a new
      // token reaches Pusher without needing to re-initialize
      authValueRef.current = authValue;

      if (clientRef.current) {
        return Promise.resolve(false);
      }

      if (pendingInitRef.current) {
        return pendingInitRef.current;
      }

      const promise = (async () => {
        const { appKey, cluster, authEndpoint } = pusherIntegrationConfig;

        try {
          await ensureSdkLoaded();
          const PusherCtor = getPusherConstructor();
          if (!PusherCtor) {
            console.error('Pusher SDK did not expose a constructor');
            return false;
          }

          const client = new PusherCtor(appKey, {
            cluster,
            channelAuthorization: {
              endpoint: authEndpoint,
              headersProvider: () => ({ Authorization: authValueRef.current ?? '' }),
            },
          });

          bindConnectEvents(client);

          clientRef.current = client;
          return true;
        } catch (error) {
          console.error('Failed to initialize Pusher SDK', error);
          return false;
        } finally {
          pendingInitRef.current = undefined;
        }
      })();

      pendingInitRef.current = promise;
      return promise;
    },
    [ensureSdkLoaded, bindConnectEvents, pusherIntegrationConfig],
  );

  const disconnectPusher = useCallback(() => {
    clientRef.current?.disconnect();
    clientRef.current = undefined;
  }, []);

  return { clientRef, ensureSdkLoaded, initializePusher, disconnectPusher } as const;
}
